import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import MainLayout from "../../../component/atoms/layout/mainLayout";
import DetailPageLayout from "../../../component/atoms/layout/detail/detailPageLayout";
import TableTitle from "../../../component/atoms/text/table/tableTitle";
import TextFormEditor from "../../../component/atoms/input/textFormEditor";
import MainInput from "../../../component/atoms/input/mainInput";
import BasicBtn from "../../../component/atoms/button/basicBtn";
import PretendardText from "../../../component/atoms/text/pretendardText";

import Header from "../../../component/organisms/header";
import Sidebar from "../../../component/organisms/sidebar";

import { route } from "../../../router/route";
import scrollToTop from "../../../util/scrollToTop";
import BoardApi from "../../../api/board.api";
import FileApi from "../../../api/file.api";
import { errorHandler } from "../../../util/errorHandler";
import { toast } from "react-toastify";
import { toastMsg } from "../../../util/toastMsg";

const SecretArchiveEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getArchiveInfo();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- 자료창고 상세 정보 ---------- //
  const [archiveInfo, setArchiveInfo] = useState();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  // 기존: { id, fileOriginFileName, fileServerFileName } / 새: { file, name, size }
  const [attachedFileList, setAttachedFileList] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getArchiveInfo = async () => {
    try {
      const response = (await BoardApi.getByIdBoard(id)).data.data.content;

      setArchiveInfo(response);
      setTitle(response?.title || "");
      setContent(response?.content || "");

      if (response?.boardPostFileList?.length > 0) {
        setAttachedFileList(
          response.boardPostFileList.map((f) => ({
            id: f.id,
            fileOriginFileName: f.fileOriginFileName,
            fileServerFileName: f.fileServerFileName,
          }))
        );
      } else {
        setAttachedFileList([]);
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  // ---------- 자료창고 수정 ---------- //
  const updateArchive = async () => {
    try {
      setIsUploading(true);

      const initialExistingIds =
        archiveInfo?.boardPostFileList?.map((f) => f.id) ?? [];
      const currentExistingIds = attachedFileList
        .filter((item) => item.id != null)
        .map((item) => item.id);
      const deleteFileIds = initialExistingIds.filter(
        (initialId) => !currentExistingIds.includes(initialId)
      );

      const newItems = attachedFileList.filter((item) => item.id == null);
      let fileList = [];

      for (let i = 0; i < newItems.length; i++) {
        const item = newItems[i];
        const res = await FileApi.fileUpload(item.file, "admin/archive");
        const data = res?.data?.data ?? res?.data ?? {};
        fileList.push({
          fileOriginFileName: data.originalFileName ?? item.name,
          fileServerFileName: data.serverFileName ?? "",
        });
      }

      await BoardApi.updateBoard(id, {
        title: title,
        content: content,
        fileList,
        deleteFileIds,
      });

      toast(toastMsg.update);
      navigate(`${route.secret_archive_detail}/${id}`, { replace: true });
      scrollToTop();
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Header />
      <MainLayout
        style={{
          position: "relative",
          gap: 306,
        }}
      >
        <Sidebar />

        {/* 본문 영역 */}
        <DetailPageLayout>
          <TableTitle>자료창고 수정</TableTitle>
          <div
            style={{
              width: "100%",
              marginBottom: 20,
            }}
          >
            <MainInput
              type="text"
              placeholder="제목을 입력해주세요"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={{
                width: "100%",
                height: 48,
                padding: "0 16px",
              }}
            />
          </div>
          <TextFormEditor
            placeholder={"상세 내용을 입력해주세요"}
            value={content}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            onChange={(e) => {
              setContent(e);
            }}
            editorHeight={600}
            style={{
              width: "100%",
            }}
          />

          {/* 첨부파일 영역 (디자인) */}
          <AttachmentSection>
            <AttachmentTitle>첨부파일</AttachmentTitle>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="*/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const input = e.target;
                const files = input?.files ? Array.from(input.files) : [];
                if (files.length > 0) {
                  setAttachedFileList((prev) => [
                    ...prev,
                    ...files.map((f) => ({
                      file: f,
                      name: f.name,
                      size: f.size ?? 0,
                    })),
                  ]);
                }
                if (input) input.value = "";
              }}
            />
            <AttachmentBox
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") e.currentTarget.click();
              }}
            >
              <img
                src="/assets/admin/icons/ico_file_file.svg"
                alt=""
                style={{ width: 24, height: 24, opacity: 0.5 }}
              />
              <PretendardText
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#808991",
                }}
              >
                클릭하여 파일을 선택하세요 (여러 개 선택 가능)
              </PretendardText>
            </AttachmentBox>
            {attachedFileList.length > 0 && (
              <FileList>
                {attachedFileList.map((file, index) => (
                  <FileListItem
                    key={file.id != null ? `existing-${file.id}` : `new-${index}`}
                  >
                    <PretendardText
                      style={{
                        fontSize: 14,
                        color: "#aaa",
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {file.fileOriginFileName ?? file.name}
                    </PretendardText>
                    <PretendardText
                      style={{ fontSize: 12, color: "rgba(181, 181, 181, 0.5)" }}
                    >
                      {file.size != null
                        ? `${(file.size / 1024).toFixed(1)} KB`
                        : ""}
                    </PretendardText>
                    <DeleteFileBtn
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAttachedFileList((prev) =>
                          prev.filter((_, i) => i !== index)
                        );
                      }}
                      aria-label="첨부파일 삭제"
                    >
                      <img
                        src="/assets/admin/icons/ico_popup_close.svg"
                        alt="삭제"
                        style={{ width: 14, height: 14 }}
                      />
                    </DeleteFileBtn>
                  </FileListItem>
                ))}
              </FileList>
            )}
            {attachedFileList.length === 0 && (
              <EmptyFileHint>
                <PretendardText
                  style={{
                    fontSize: 14,
                    color: "rgba(181, 181, 181, 0.5)",
                  }}
                >
                  첨부된 파일이 없습니다.
                </PretendardText>
              </EmptyFileHint>
            )}
          </AttachmentSection>

          <div
            style={{
              width: "100%",
              height: 79,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
              gap: 12,
            }}
          >
            <BasicBtn onClick={updateArchive} disabled={isUploading}>
              {isUploading ? "업로드 중..." : "업로드하기"}
            </BasicBtn>
          </div>
        </DetailPageLayout>
      </MainLayout>
    </>
  );
};

export default SecretArchiveEdit;

const AttachmentSection = styled.section`
  width: 100%;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const AttachmentTitle = styled(PretendardText)`
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  color: #f3f3f3;
  margin-bottom: 12px;
`;

const AttachmentBox = styled.div`
  width: 100%;
  min-height: 120px;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const FileList = styled.ul`
  width: 100%;
  margin-top: 12px;
  padding: 0;
  list-style: none;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  overflow: hidden;
`;

const FileListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);

  &:last-child {
    border-bottom: none;
  }
`;

const DeleteFileBtn = styled.button`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  color: #808991;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #cf6679;
  }
`;

const EmptyFileHint = styled.div`
  width: 100%;
  margin-top: 12px;
  padding: 16px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.02);
`;
